// client/src/pages/auth/Login.js
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import api from '../../services/api.js';
import Input from '../../components/ui/Input.js';
import Button from '../../components/ui/Button.js';
import Modal from '../../components/ui/Modal.js';
import ProgressSteps from '../../components/ui/ProgressSteps.js';

const steps = [
  { id: 'account', label: 'Log In' },
  { id: 'choose-plan', label: 'Confirm Plan' },
  { id: 'payment', label: 'Payment' },
];

export default function Login() {
  const { user, login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // "Stay logged in" — defaults to true so the common case (a returning user
  // on their own device) gets the long 30-day session. Unchecking gives a
  // short session that lapses sooner when they stop using the app.
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [trialMessage, setTrialMessage] = useState('');

  // Soft-delete restore state
  const [showRestore, setShowRestore] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  // Check if user came from pricing page
  const pendingPlan = (() => {
    try {
      const stored = sessionStorage.getItem('pendingPlan');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  // Check if user came from a free trial button
  const pendingTrial = sessionStorage.getItem('pendingTrial');

  // Check for ?redirect= query param. Used by flows that send a logged-out
  // user to log in first (e.g. gift redemption: /login?redirect=/gift/redeem/XXX).
  // After login completes, send them back where they were headed.
  // Only honored for same-origin paths (must start with `/`) so an attacker
  // can't craft a redirect to a phishing site by passing a full URL.
  const redirectParam = searchParams.get('redirect');
  const safeRedirect = redirectParam && redirectParam.startsWith('/') ? redirectParam : null;

  // After login, once user data loads, redirect based on their current plan
  useEffect(() => {
    const shouldRedirect = sessionStorage.getItem('pendingPlanRedirect');
    if (!shouldRedirect || !user || !pendingPlan) return;

    sessionStorage.removeItem('pendingPlanRedirect');

    if (user.tier?.ispaid || user.usertype >= 2) {
      navigate(`/account/change-plan?tier=${pendingPlan.tier}&interval=${pendingPlan.interval}`);
    } else {
      navigate(`/checkout?tier=${pendingPlan.tier}&interval=${pendingPlan.interval}`);
    }
  }, [user, pendingPlan, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setTrialMessage('');

    try {
      await login(email, password, rememberMe);

      // Priority 1: User clicked "Start Free Trial" on pricing, then clicked "Sign in"
      if (pendingTrial) {
        const tierId = parseInt(pendingTrial);
        sessionStorage.removeItem('pendingTrial');

        try {
          await api.post('/auth/activate-trial', { tierId });
          // Trial activated successfully — go home with reload to pick up new user data
          window.location.href = '/';
          return;
        } catch (trialErr) {
          // Trial already used or not available — show message briefly, then redirect
          const msg = trialErr.data?.error || 'Free trial is no longer available.';
          setTrialMessage(msg);
          setLoading(false);
          setTimeout(() => {
            navigate('/');
          }, 3000);
          return;
        }
      }

      // Priority 2: User came from pricing with a plan selected.
      // Defer the navigation until user data finishes loading (the useEffect
      // above handles the routing) because we need to know the user's current
      // plan to choose between /checkout and /account/change-plan.
      if (pendingPlan) {
        sessionStorage.setItem('pendingPlanRedirect', 'true');
        return;
      }

      // Priority 3: User came in via a redirect link (e.g. gift redemption).
      // Send them back to where they were headed before being asked to log in.
      if (safeRedirect) {
        navigate(safeRedirect);
        return;
      }

      // Default: send to home
      navigate('/');
    } catch (err) {
      if (err.data?.error === 'account_deleted') {
        setDeleteData(err.data);
        setShowRestore(true);
      } else {
        setError(err.data?.error || 'Login failed');
      }
    }
    setLoading(false);
  };

  const handleRestore = async () => {
    try {
      const res = await fetch('/api/auth/restore-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.token) {
        await loginWithToken(data.token);
        navigate('/');
      }
    } catch {
      setError('Restore failed');
    }
  };

  // Did the user arrive specifically via a gift redemption link?
  // Used to show a tailored badge and message so the gift context isn't lost.
  const isGiftRedirect = safeRedirect && safeRedirect.startsWith('/gift/redeem/');

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-md mx-auto px-4 py-12">
        {/* Only show progress steps if user came from pricing */}
        {pendingPlan && <ProgressSteps steps={steps} currentStep="account" />}

        <h1 className="font-display text-3xl text-gold-dark dark:text-gold text-center mb-2">Welcome Back</h1>

        {/* Show selected plan badge if coming from pricing */}
        {pendingPlan && (
          <div className="text-center mb-4">
            <span className="inline-block bg-gold/10 border border-gold/30 text-gold text-sm px-3 py-1 rounded-full">
              Selected: {pendingPlan.tierName} — {pendingPlan.interval}
            </span>
          </div>
        )}

        {/* Show trial badge if coming from free trial button */}
        {pendingTrial && !pendingPlan && (
          <div className="text-center mb-4">
            <span className="inline-block bg-gold/10 border border-gold/30 text-gold text-sm px-3 py-1 rounded-full">
              ✨ Free trial — no credit card required
            </span>
          </div>
        )}

        {/* Show gift badge if logging in via a gift redemption link */}
        {!pendingPlan && !pendingTrial && isGiftRedirect && (
          <div className="text-center mb-4">
            <span className="inline-block bg-gold/10 border border-gold/30 text-gold text-sm px-3 py-1 rounded-full">
              🎁 Log in to claim your gift
            </span>
          </div>
        )}

        <p className="text-royal-500 dark:text-royal-200 text-center mb-8">
          {pendingPlan
            ? 'Log in to continue to payment'
            : pendingTrial
              ? 'Log in to activate your free trial'
              : isGiftRedirect
                ? 'Log in to continue to your gift'
                : ''}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg text-red-600 dark:text-red-300 text-sm">{error}</div>
        )}

        {/* Trial already used message — shows briefly before redirect */}
        {trialMessage && (
          <div className="mb-4 p-4 bg-royal-900 dark:bg-royal-800/60 border border-gold/30 rounded-xl text-center">
            <p className="text-gold text-sm font-medium mb-1">{trialMessage}</p>
            <p className="text-royal-100 text-sm">Redirecting to home...</p>
          </div>
        )}

        {!trialMessage && (
          <form onSubmit={handleSubmit} className="bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl p-6">
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />

            {/* Stay logged in — long (30d) vs short session */}
            <label className="flex items-center gap-2 mb-5 mt-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-royal-500 bg-royal-800 text-gold focus:ring-gold focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-royal-100">Stay logged in for 30 days</span>
            </label>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        )}

        {!trialMessage && (
          <div className="text-center mt-6 space-y-2">
            <Link to="/forgot-password" className="text-royal-500 dark:text-royal-200 hover:text-gold-dark dark:hover:text-gold text-sm block">Forgot password?</Link>
            {/* Register link — preserve the redirect so users who click
                "Register" don't lose their place in the gift redemption flow. */}
            <p className="text-royal-500 dark:text-royal-200 text-sm">
              No account?{' '}
              <Link
                to={safeRedirect ? `/register?redirect=${encodeURIComponent(safeRedirect)}` : '/register'}
                className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light"
              >
                Register
              </Link>
            </p>
          </div>
        )}

        {/* Restore account modal */}
        <Modal isOpen={showRestore} onClose={() => setShowRestore(false)} title="Account Scheduled for Deletion">
          <p className="text-royal-100 mb-4">
            Your account will be permanently deleted on{' '}
            <strong className="text-white">
              {deleteData?.expiresAt ? new Date(deleteData.expiresAt).toLocaleDateString() : 'soon'}
            </strong>.
          </p>
          <p className="text-royal-100 mb-6">Want to restore your account?</p>
          <div className="flex gap-3">
            <Button onClick={handleRestore}>Restore My Account</Button>
            <Button variant="ghost" onClick={() => setShowRestore(false)}>Continue with Deletion</Button>
          </div>
        </Modal>
      </div>
    </div>
  );
}